import { ChangeDetectionStrategy, Component, HostBinding, OnChanges, OnInit, Signal, TemplateRef, ViewChild, inject } from '@angular/core';

import { EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { CrmConfig, EpcotConfig, MarqueeConfig, Sitecore, SitecoreTokenCardDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { DialogAnimation, ModalDialogService, ModalRef } from '@frontend/sports/modal/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { MarketUrlParam, SportUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { NativePriceFormatterService } from '@frontend/sports/odds/feature/native-price';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { DeviceService, MenuAction, MenuActionsService, NativeAppService, WINDOW } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { IBetslipRootState } from 'packages/sports/common/betslip/base/store/state';
import { TokenQuickViewDetailsComponent } from 'packages/sports/common/betslip/modules/reward-tokens/components/digital/card-details/token-quick-view-details.component';
import { IRewardToken } from 'packages/sports/common/betslip/modules/reward-tokens/reward-tokens.model';
import { selectRewardTokensList } from 'packages/sports/common/betslip/modules/reward-tokens/selectors';
import { RewardTokensBannerService } from 'packages/sports/common/betslip/modules/reward-tokens/services/reward-tokens-banner.service';
import { firstValueFrom } from 'rxjs';

import { BetBuilderConfigService } from '../betbuilder/services/betbuilder-config.service';
import { getBetbuilderTypeValue, isBetbuilderEvent } from '../betbuilder/utils';
import { CCBEventFeature, CCBEventService } from '../common/ccb-event.service';
import { ComponentProxy } from '../deferred/component-loader.service';
import { BetBuilderTrackingValues } from '../event-details/bet-builder-v2/bet-builder-tracking';
import { BetBuilderOverlayService } from '../event-details/bet-builder-v2/services/bet-builder-overlay.service';
import { NavigationRouterService } from '../navigation/navigation-router.service';
import { PopupManager, PopupTypes } from '../popup/popup-manager.service';
import { HighlightsCardComponent } from './highlights-card.component';
import { OptionGroupBinding, SpecialMarqueeBadgeBinding } from './highlights-marquee-component-loader.service';
import { HighlightsMarqueePageService } from './highlights-marquee-page.service';
import { FixtureTile, NameVisibility } from './marquee-tile.model';

export enum DisplayType {
    Link,
    Popup,
    View,
}

@Component({
    selector: 'ms-highlights-special-card',
    templateUrl: 'highlights-special-card.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class HighlightsSpecialCardComponent extends HighlightsCardComponent<FixtureTile> implements OnInit, OnChanges {
    previousOdds?: string;
    url: string;
    optionGroup?: EventOptionGroup;
    optionGroupNames: string[];
    eventName: string;
    target: string;
    displayTypeEnum = DisplayType;
    displayType: DisplayType;
    isPriceBoosted = false;
    marqueeOptionGroupComponent: ComponentProxy<OptionGroupBinding>;
    specialMarqueeBadgeComponent: ComponentProxy<SpecialMarqueeBadgeBinding>;
    @HostBinding('class') className = 'card-bullet-view';
    @HostBinding('class.epcot-enabled') isEpcot: string;
    @ViewChild('ogpOverlayHeader') ogpOverlayHeaderTemplate: TemplateRef<any>;
    private modalHandler: ModalRef | null = null;
    private token: IRewardToken | undefined;
    tokenList: Signal<IRewardToken[]>;
    isEpcotEnabled: boolean;
    private readonly menuActionService = inject(MenuActionsService);
    private readonly _window = inject(WINDOW);
    tracking: TrackingSource;

    private ccbEventService = inject(CCBEventService);

    constructor(
        public sitecore: Sitecore,
        protected marqueeConfig: MarqueeConfig,
        protected nativePriceFormatter: NativePriceFormatterService,
        protected navigationRouter: NavigationRouterService,
        protected override urlHelper: UrlHelperService,
        protected nativeAppService: NativeAppService,
        protected override highlightsMarqueePageService: HighlightsMarqueePageService,
        protected popupManager: PopupManager,
        private betBuilderOverlayService: BetBuilderOverlayService,
        private betBuilderConfigService: BetBuilderConfigService,
        private dialogService: ModalDialogService,
        private store: Store<IBetslipRootState>,
        private deviceService: DeviceService,
        private sitecoreCardDetailsConfig: SitecoreTokenCardDetailsConfig,
        private tokensConfig: CrmConfig,
        private rewardTokensBannerService: RewardTokensBannerService,
        protected epcotConfig: EpcotConfig,
        protected redirectHelper: RedirectHelperService,
    ) {
        super('SpecialMarquee', highlightsMarqueePageService, urlHelper);
        this.tokenList = this.store.selectSignal(selectRewardTokensList);
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    override ngOnInit(): void {
        if (this.tile.imageUrl) {
            this.trackingIdentifier = 'SpecialMarqueeIMG';
        }
        super.ngOnInit();
        this.isEpcot = this.isEpcotEnabled ? 'epcot-enabled' : '';
    }

    ngOnChanges(changes: ISimpleChanges<HighlightsSpecialCardComponent>): void {
        if (!changes.tile) {
            return;
        }
        this.url = !this.tile.isFromPortal ? this.getUrl() : '';

        this.eventName = this.getEventName();

        if (this.tile.previousNativePrice) {
            this.previousOdds = this.nativePriceFormatter.format(this.tile.previousNativePrice);
        } else {
            this.previousOdds = undefined;
        }
        if (this.tile.event) {
            this.optionGroup = this.getOptionGroup();
            this.marqueeOptionGroupComponent?.update({
                event: this.tile.event,
                optionGroup: this.optionGroup,
            });
        }
        this.optionGroupNames = this.getOptionGroupNames();

        this.displayType =
            this.preventNavigation && !this.shouldShowOGPOverlay()
                ? DisplayType.View
                : this.isPopupLink || this.shouldShowOGPOverlay()
                  ? DisplayType.Popup
                  : DisplayType.Link;

        this.specialMarqueeBadgeComponent?.update({ tile: this.tile });

        this.tracking = {
            source: this.trackingSource,
            position: this.trackingPosition,
            marqueeName: this.tile.trackingDetail?.marqueeName,
            sitecoreTemplateId: this.tile.trackingDetail?.sitecoreTemplateId,
            marqueeType: this.tile.source?.marqueeType,
            isAutomatedMarquee: this.tile.source?.isAutomatedMarquee,
        };
    }

    tileClick(): void {
        this.navigationRouter.resetNavigation();
        if (this.tile.source?.isRAFMarquee) {
            this.menuActionService.invoke(MenuAction.TOGGLE_REFERRED_FRIENDS, '');
            return;
        }

        this.token = this.tile.crmPromotionId ? this.tokenList().find((t) => t.id === this.tile.crmPromotionId) : undefined;

        if (this.tile.isFromPortal) {
            const eventUrl = this.urlHelper.getEventUrl(this.tile.event);
            this._window.parent.postMessage({ eventUrl }, '*');
        } else if (this.token?.customDescription && this.tokensConfig.tokens.isRewardDrawerEnabled && this.tokensConfig.tokens.isQuickViewEnabled) {
            this.showQuickViewPopup();
        } else {
            if (!this.ccbEventService.sendCCBToNative(this.tile.link, CCBEventFeature.Marquee)) {
                this.redirectHelper.goToPage(this.url);
            }
        }
    }

    private async showQuickViewPopup() {
        await firstValueFrom(this.sitecoreCardDetailsConfig.whenReady);
        this.closeModal();
        const tokenUrlDetails = this.rewardTokensBannerService.getTokenUrlDetails(this.token!);
        if (!this.modalHandler) {
            this.modalHandler = this.dialogService.openDialog(TokenQuickViewDetailsComponent, {
                data: {
                    token: this.token,
                    tokenInfoUrl: tokenUrlDetails.tokenInfoUrl,
                    tokenTitle: this.rewardTokensBannerService.getTokenTitle(this.token!),
                    categories: this.rewardTokensBannerService.getTokenCategories(this.token!),
                    promoHubUrlTokenType: tokenUrlDetails.promoHubUrlTokenType,
                },
                settings: {
                    fit: false,
                    showPageHeader: false,
                    showDialogHeader: false,
                    isModalDialog: true,
                    animation: DialogAnimation.SlideInFromBottom,
                    closeAnimation: DialogAnimation.SlideOutFromTop,
                    cssClass: `${this.deviceService.isMobile ? 'reward-token-card-details-mobile' : ''} reward-token-card-details-quick-view`,
                    backdropClass: 'reward-token-card-details-modal-backdrop',
                },
            });
        }
    }

    private closeModal(): void {
        if (this.modalHandler) {
            this.modalHandler.close();
            this.modalHandler = null;
        }
    }

    openPopup(): void {
        if (this.shouldShowOGPOverlay()) {
            this.openEntainBetBuilderOverlay();
        } else {
            this.popupManager.toggle(<PopupTypes>this.tile.link!.popup);
        }
    }

    private shouldShowOGPOverlay(): boolean {
        return (
            this.betBuilderConfigService.showEntainUiInOverlay(this.tile.event?.sport.id) &&
            !!this.tile.ogpOverlay &&
            isBetbuilderEvent(this.tile?.event, this.betBuilderConfigService)
        );
    }

    private get isPopupLink(): boolean {
        return (this.tile.link && !!this.tile.link.popup) ?? false;
    }

    private getOptionGroup(): EventOptionGroup | undefined {
        const optionGroup = this.tile.event.optionGroups.find((group) => group.id === this.tile.optionGroupId.toString());

        if (optionGroup) {
            optionGroup.options = optionGroup.options.filter((option) => option.id === this.tile.optionId);
        }

        return optionGroup;
    }

    private getUrl(): string {
        if (this.shouldShowOGPOverlay()) {
            return '';
        }
        if (this.tile.event) {
            if (!this.betBuilderConfigService.showEntainUiInOverlay(this.tile.event.sport.id) && this.tile.ogpOverlay) {
                return this.tile.link?.href
                    ? this.urlHelper.getUrl(this.tile.link.href)
                    : this.urlHelper.getEventUrl(this.tile.event, getBetbuilderTypeValue(this.tile.event, MarketUrlParam));
            }

            if (this.marqueeConfig.specialLeagueIds.indexOf(this.tile.event.league.id) > -1) {
                return this.urlHelper.getSportUrl(this.tile.event.sport, SportUrlParam.Betting, this.tile.event.region, this.tile.event.league);
            }

            return this.urlHelper.getEventUrl(this.tile.event);
        } else {
            this.preventNavigation = false;

            if (!this.isPopupLink) {
                this.setCustomLinkData();

                return this.urlHelper.getUrl(this.tile.link!.href);
            }

            return '';
        }
    }

    private setCustomLinkData(): void {
        this.target = this.tile.link!.target !== '' ? this.tile.link!.target : '_blank';

        const nav = <any>this._window.navigator;
        if (!!nav.standalone || this.nativeAppService.isNative) {
            this.target = '_self';
        }
    }

    private getOptionGroupNames(): string[] {
        switch (this.tile.marketAndOptionNameVisibility) {
            case NameVisibility.WithoutOverride: {
                return this.getMarketAndOptionNameWithoutOverride();
            }
            case NameVisibility.WithOverride: {
                if (!this.tile.marketAndOptionNameOverride && !this.tile.event) {
                    return [];
                }

                return this.getDefaultMarketAndOptionName();
            }
            case NameVisibility.DoNotShow: {
                return [];
            }
            default: {
                return this.getDefaultMarketAndOptionName();
            }
        }
    }

    private getEventName(): string {
        switch (this.tile.eventNameVisibility) {
            case NameVisibility.WithoutOverride: {
                return this.tile.event ? this.tile.event.name : '';
            }
            case NameVisibility.WithOverride: {
                if (!this.tile.eventNameOverride && !this.tile.event) {
                    return '';
                }

                return this.getDefaultEventName();
            }
            case NameVisibility.DoNotShow: {
                return '';
            }
            default: {
                return this.getDefaultEventName();
            }
        }
    }

    private getDefaultEventName(): string {
        return this.tile.eventNameOverride ? this.tile.eventNameOverride : this.tile.event && this.tile.event.name;
    }

    private getDefaultMarketAndOptionName(): string[] {
        if (this.tile.marketAndOptionNameOverride) {
            return this.isEpcotEnabled ? this.tile.marketAndOptionNameOverride.split('|') : [this.tile.marketAndOptionNameOverride];
        }

        return this.getMarketAndOptionNameWithoutOverride();
    }

    private getMarketAndOptionNameWithoutOverride(): string[] {
        return this.optionGroup && this.optionGroup.options.length > 0 ? [`${this.optionGroup.options[0].name} - ${this.optionGroup.name}`] : [];
    }

    private openEntainBetBuilderOverlay() {
        this.betBuilderOverlayService.openBuilder({
            headerTemplate: this.ogpOverlayHeaderTemplate,
            event: this.tile.event,
            source: BetBuilderTrackingValues.EventInfo,
            isOverlay: true,
        });
    }
}
